import type { ReactNode } from 'react';
import {
  CategoryBarChart,
  ChartCard,
  DailySeriesChart,
  formatCount,
  formatUsd,
  CHART_SEMANTIC,
  MONEY,
} from '@kioskgaming/ui';
import MetricStrip from './MetricStrip';
import { channelMixFromKpis } from './chartHelpers';
import type { AgentDashboardFinancialData, DashboardV2Period } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  data: AgentDashboardFinancialData | null;
  isLoading: boolean;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-blue-900/70">{title}</h2>
    {children}
  </section>
);

export default function FinancialTab({ data, isLoading }: Props) {
  const kpis = data?.kpis;
  const earnedMix = (data?.charts?.earnedMix ?? []).filter((d) => d.value > 0);
  const dailyNet = data?.charts?.dailyNetPosition ?? [];

  const moneyInByChannel = channelMixFromKpis([
    { name: 'Online deposits', kpi: kpis?.onlineDepositsNetUsd },
    { name: 'Counter deposits', kpi: kpis?.counterDepositsCashReceived },
  ]);

  const payoutsByChannel = channelMixFromKpis([
    { name: 'Online withdrawals', kpi: kpis?.onlineWithdrawalsGrossUsd },
    { name: 'Counter redemptions', kpi: kpis?.counterRedemptionsCashPaid },
  ]);

  return (
    <div className="space-y-4">
      <MetricStrip
        title="Performance"
        columnsClassName="grid-cols-2 sm:grid-cols-4 xl:grid-cols-8"
        items={[
          {
            id: 'net',
            label: 'Net position',
            tooltip: 'Earned minus spent for the selected period.',
            kpi: kpis?.netPosition,
            format: 'currency',
            emphasis: true,
            barClassName: 'bg-violet-100',
            valueClassName: 'text-violet-900',
          },
          {
            id: 'earned',
            label: 'Earned',
            tooltip: 'USD earned selling credits directly to your Shops.',
            kpi: kpis?.earnedTotal,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'spent',
            label: 'Spent',
            tooltip: 'USD you paid your Super Agent for credits.',
            kpi: kpis?.spentTotal,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'cashShops',
            label: 'Cash from Shops',
            tooltip: 'Cash your Shops paid you for credits.',
            kpi: kpis?.cashFromShops,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'cdn',
            label: 'Credits sold online',
            tooltip: 'USD from Shops buying credits online.',
            kpi: kpis?.cdnSoldToShops,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'converts',
            label: 'Shop converts',
            tooltip: 'USD from Shops converting balance into credits.',
            kpi: kpis?.convertsFromShops,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'purchased',
            label: MONEY.creditsBought,
            tooltip: 'USD you paid your Super Agent for credits (online / convert).',
            kpi: kpis?.creditsPurchasedUsd,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'cashToSuperAgent',
            label: 'Cash paid to your Super Agent',
            tooltip: 'Cash you paid your Super Agent for credit purchases.',
            kpi: kpis?.cashPaidUpstream,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
        ]}
      />

      <MetricStrip
        title="Network Player money"
        columnsClassName="grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"
        items={[
          {
            id: 'playerIn',
            label: 'Player money in',
            tooltip: 'Online deposits plus counter cash from Shop-linked Players.',
            kpi: kpis?.playerMoneyIn,
            format: 'currency',
            barClassName: 'bg-blue-100',
          },
          {
            id: 'onlineDep',
            label: 'Online deposits',
            tooltip: 'Net USD from online Player deposits.',
            kpi: kpis?.onlineDepositsNetUsd,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'onlineWd',
            label: 'Online withdrawals',
            tooltip: 'Gross USD paid out to Players online.',
            kpi: kpis?.onlineWithdrawalsGrossUsd,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'counterDep',
            label: 'Counter deposits',
            tooltip: 'Cash received at Shop counters.',
            kpi: kpis?.counterDepositsCashReceived,
            format: 'currency',
            barClassName: 'bg-blue-100',
          },
          {
            id: 'counterRdm',
            label: 'Counter redemptions',
            tooltip: 'Cash paid out at Shop counters.',
            kpi: kpis?.counterRedemptionsCashPaid,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'funding',
            label: 'Payout funding',
            tooltip: 'USD your Shops spent funding Player online withdrawals.',
            kpi: kpis?.tierFundingUsd,
            format: 'currency',
            barClassName: 'bg-blue-100',
          },
        ]}
      />
      <p className="text-xs text-muted-foreground">
        {formatCount(kpis?.onlineDepositsCount?.value ?? 0)} online deposits ·{' '}
        {formatCount(kpis?.onlineWithdrawalsCount?.value ?? 0)} online withdrawals
      </p>

      <Section title="Trends">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <ChartCard
            title="Daily net position"
            tooltip="Daily earned minus spent for your Agent trading P&L."
            loading={isLoading}
            empty={!isLoading && dailyNet.every((d) => d.net === 0)}
          >
            <div className="h-64">
              <DailySeriesChart
                data={dailyNet}
                xKey="date"
                height={256}
                series={[
                  { key: 'earned', label: 'Earned', color: CHART_SEMANTIC.moneyIn },
                  { key: 'spent', label: 'Spent', color: CHART_SEMANTIC.moneyOut },
                  { key: 'net', label: 'Net', color: CHART_SEMANTIC.profit },
                ]}
                valueFormatter={formatUsd}
              />
            </div>
          </ChartCard>
          <ChartCard
            title="Earned mix"
            tooltip="How your earned USD broke down this period (cash / online / converts)."
            loading={isLoading}
            empty={!isLoading && earnedMix.length === 0}
            emptyLabel="No earnings in this period"
          >
            <CategoryBarChart data={earnedMix} color={CHART_SEMANTIC.moneyIn} valueFormatter={formatUsd} />
          </ChartCard>
          <ChartCard
            title="Money in by channel"
            tooltip="Player money into your Shops this period: online deposits vs counter cash."
            loading={isLoading}
            empty={!isLoading && moneyInByChannel.length === 0}
            emptyLabel="No Player money in this period"
          >
            <CategoryBarChart
              data={moneyInByChannel}
              color={CHART_SEMANTIC.moneyIn}
              valueFormatter={formatUsd}
            />
          </ChartCard>
          <ChartCard
            title="Payouts by channel"
            tooltip="Money leaving your Shops this period: online withdrawals vs counter redemptions."
            loading={isLoading}
            empty={!isLoading && payoutsByChannel.length === 0}
            emptyLabel="No payouts in this period"
          >
            <CategoryBarChart
              data={payoutsByChannel}
              color={CHART_SEMANTIC.moneyOut}
              valueFormatter={formatUsd}
            />
          </ChartCard>
        </div>
      </Section>
    </div>
  );
}
